import { Card } from '@/components/ui/card';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { cn } from '@/lib/utils';
import type { RecurringGroupKey, RecurringPeriodSummary } from '@/lib/api';
import { useRecurringTaxonomyColors } from './taxonomy';

const MONTHS_PT = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

/** Display order + short labels for the hero bar/legend (matches the year chart stack). */
const ORDER: { key: RecurringGroupKey; label: string }[] = [
  { key: 'fixas', label: 'Fixas' },
  { key: 'parcelamentos', label: 'Parcelas' },
  { key: 'assinaturas', label: 'Assinaturas' },
];

/** "Pagamentos do período" hero: pago vs previsto with a segmented bar + type legend. */
export function PeriodSummaryCard({ period }: { period: RecurringPeriodSummary }) {
  const formatCurrency = useFormatCurrency();
  const taxonomyColors = useRecurringTaxonomyColors();
  const monthName = MONTHS_PT[Math.min(Math.max(period.month - 1, 0), 11)];
  const pctPago = period.previsto > 0 ? period.pago / period.previsto : 0;

  const valueFor = (key: RecurringGroupKey) =>
    period.breakdown.find((slice) => slice.key === key)?.value ?? 0;

  return (
    <Card variant="flat" className="rounded-[var(--radius-3xl)] border-border bg-surface p-5 shadow-[var(--shadow-1)] sm:p-6">
      <div className="mb-3 flex items-center justify-between gap-3">
        {/* No `capitalize` here: CSS capitalizes EVERY word, so the sentence came out as
            "Pagamentos De Agosto". A lowercase month is the correct pt-BR form. */}
        <p className="text-sm text-text-tertiary sm:text-xs">Pagamentos de {monthName}</p>
        <span className="font-mono text-xs text-text-tertiary tabular-nums">{Math.round(pctPago * 100)}% pago</span>
      </div>

      <div className="mb-3 flex items-baseline gap-2">
        <span className="font-sans text-[32px] font-semibold leading-none text-success-text tabular-nums">
          {formatCurrency(period.pago, { noCents: true })}
        </span>
        <span className="font-mono text-sm text-text-tertiary tabular-nums">
          / {formatCurrency(period.previsto, { noCents: true })}
        </span>
      </div>

      <div className="flex h-2.5 overflow-hidden rounded-full bg-divider" aria-hidden>
        {ORDER.map(({ key }) => {
          const value = valueFor(key);
          const widthPct = period.previsto > 0 ? (value / period.previsto) * pctPago * 100 : 0;
          return widthPct > 0 ? (
            <span key={key} className="h-full" style={{ width: `${widthPct}%`, backgroundColor: taxonomyColors[key] }} />
          ) : null;
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {ORDER.map(({ key, label }) => (
          <span key={key} className={cn('flex items-center gap-1.5 text-xs text-text-secondary')}>
            <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: taxonomyColors[key] }} />
            {label}
            <span className="font-mono text-text-tertiary tabular-nums">{formatCurrency(valueFor(key), { noCents: true })}</span>
          </span>
        ))}
      </div>
    </Card>
  );
}
